/**
 * ResetPasswordConfirm — consume token de query param + fija nueva password.
 *
 * Token llega como ?token=XXX desde el link del email. Tras éxito,
 * navegamos a /login con location.state.successMessage para mostrar
 * "Hecho. Entra con tu nueva contraseña."
 */
import { useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { AuthLayout } from "@/components/ui/AuthLayout";
import { Button } from "@/components/ui/Button";
import { FormError } from "@/components/ui/FormError";
import { Input } from "@/components/ui/Input";
import { confirmReset } from "@/api/auth";
import { t, translateError } from "@/i18n/es";
import { validatePassword } from "@/lib/validators";
import { ApiError } from "@/types/api";

export function ResetPasswordConfirm() {
  const [searchParams] = useSearchParams();
  const token = useMemo(() => searchParams.get("token") ?? "", [searchParams]);
  const navigate = useNavigate();

  const [newPassword, setNewPassword] = useState("");
  const [passErr, setPassErr] = useState<string | undefined>();
  const [formErr, setFormErr] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Sin token en la URL es un caso "link inválido" desde el inicio.
  if (!token) {
    return (
      <AuthLayout
        title={t.auth.resetConfirmTitle}
        footer={
          <Link to="/reset-password" className="hover:text-bone">
            {t.auth.toReset}
          </Link>
        }
      >
        <FormError message={translateError("token_invalid")} />
      </AuthLayout>
    );
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setFormErr(null);

    const passErrCode = validatePassword(newPassword);
    setPassErr(passErrCode ? translateError(passErrCode.code) : undefined);
    if (passErrCode) return;

    setSubmitting(true);
    try {
      await confirmReset({ token, new_password: newPassword });
      navigate("/login", {
        replace: true,
        state: { successMessage: t.auth.passwordChanged },
      });
    } catch (err) {
      if (err instanceof ApiError && err.field === "password") {
        setPassErr(translateError(err.code));
      } else {
        const code = err instanceof ApiError ? err.code : "network_error";
        setFormErr(translateError(code));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthLayout
      title={t.auth.resetConfirmTitle}
      footer={
        <Link to="/login" className="hover:text-bone">
          {t.auth.backToLogin}
        </Link>
      }
    >
      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
        <FormError message={formErr} />
        <Input
          label={t.auth.newPasswordLabel}
          type="password"
          autoComplete="new-password"
          autoFocus
          required
          placeholder={t.auth.passwordPlaceholder}
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          error={passErr}
        />
        <Button type="submit" loading={submitting} className="mt-2">
          {t.auth.confirmResetCta}
        </Button>
      </form>
    </AuthLayout>
  );
}
